/**
 * Runtime v3 — the `derive` plan kind (increment 10a, #7764).
 *
 * WHY A PLAN KIND AND NOT A HELPER. The anti-fabrication fence binds a
 * published figure to a governed RUN. Arithmetic done anywhere else — in the
 * model's head, in a narrator, in a formatter — is unbindable by construction,
 * so the twelve NO_DEFINITION cases the oracle replay found (a +10% target, a
 * gap, a residual, a share, a growth rate) could never be published however
 * obviously true they were. `derive` makes the arithmetic a run: it has an
 * accepted plan, resolved references, a result digest, a lineage naming the
 * runs it was computed from, and a replay that reproduces it.
 *
 * THE OPERATOR SET IS CLOSED AT SIX and a seventh is a ruling, not a default.
 * Four are shape-free arithmetic over figures the runtime already published;
 * the fifth is period-shaped and is the only one that reaches the store again;
 * the sixth is a SELECTION, ruled in by #8071 on the GL26 demand the census
 * confirmed: `extrema` computes no number of its own — it republishes the least
 * and greatest cells of one series with the coordinate each occurred at.
 * Anything statistical — a forecast, a seasonal index, a counterfactual — is a
 * declared method with its own definition, never an emergent composition of
 * these six.
 *
 * THE MODEL NEVER SUPPLIES A VALUE. An input is a RUN ID plus the ref of a
 * figure that run published. The tool reads the figure from the run store, so
 * a model that mistypes a number cannot make the arithmetic agree with it, and
 * a model that invents a run id gets `run_not_found` rather than an answer.
 *
 * THE OPERANDS ARE RECORDED ON THE PLAN. They are resolved once, at admission,
 * and stored — exactly as a query plan stores the revision and the resolved
 * window. A replay therefore reproduces the figure from the plan alone: it can
 * neither drift onto a later edit of an input run nor fail because an input
 * run was pruned. The input run ids stay on the lineage, so the trail back is
 * intact either way.
 */

import type { ExactDecimalString, IsoInstant } from './common';
import type { RuntimeV3ResultClass } from './result-value-policy';
import type { RuntimeV3RequestedResultClass } from './analytical-request';

/**
 * #8351 PR 1 — A RECIPE THE COMPILER MAY LIFT ITSELF, never the model.
 *
 * `operand_total` is the one recipe PR 1 lowers: a `total` over the named
 * operand, at the owning output's own resolved period (`periodFrom: 'owner'`
 * is the only value today — no recipe reaches for another output's window).
 * Declared here, the derive plan's own leaf module, so `operation-registry.ts`
 * (which already imports FROM this file for the derive operator set) can
 * import the type without a cycle.
 */
export type RuntimeV3OperandRecipe = Readonly<{ kind: 'operand_total'; periodFrom: 'owner' }>;

/**
 * A CLOSED SET OF SERVER-OWNED SEMANTIC CONSTRAINTS between two roles of one
 * derived output, or between a role and a fixed set of requested result
 * classes. The first four are REQUEST-level (checked at validation, before
 * any leg runs, #8351 Y2); `same_resolved_population_as` cannot be — no
 * request-level field says which population/scope a role will resolve to —
 * so it is checked AFTER resolution but before any leg runs (#8351 HOLD cure
 * AR-01/finding 5, `analytical-derived-output-executor.ts`), never at
 * `analytical-request-validation.ts`, which treats it as a no-op there.
 */
export type RuntimeV3RoleConstraint =
  | Readonly<{ kind: 'same_family_as'; role: string }>
  | Readonly<{ kind: 'same_basis_as'; role: string }>
  | Readonly<{ kind: 'same_result_class_as'; role: string }>
  | Readonly<{ kind: 'result_class_in'; classes: readonly RuntimeV3RequestedResultClass[] }>
  | Readonly<{ kind: 'same_resolved_population_as'; role: string }>;

/**
 * ONE ROLE A DERIVED OUTPUT MAY MINT AN OPERAND FOR. Order is the model's own
 * meaning (a `subtract` reads `minuend` minus `subtrahend`) — declared here as
 * an ORDERED array, never a set, so the compiler and the label never have to
 * re-derive which role came first.
 */
export type RuntimeV3RoleRequirement = Readonly<{
  role: string;
  recipe: RuntimeV3OperandRecipe;
  constraints: readonly RuntimeV3RoleConstraint[];
}>;

export const RUNTIME_V3_DERIVE_OPERATORS = [
  /** One figure multiplied by a declared constant. The unit is unchanged. */
  'scale',
  /** The first figure minus the second. Both must carry the same unit. */
  'subtract',
  /** The first figure over the second. The result is a ratio. */
  'ratio',
  /**
   * The first figure over the total of every listed figure.
   *
   * The total is NOT added up here: it is re-read as a governed total of the
   * same run's own plan, so the additivity rules that decide whether such a
   * total may be published at all (#7954) decide it for this denominator too.
   */
  'share_of_total',
  /**
   * One figure against the same plan a declared number of buckets earlier.
   *
   * The prior figure is produced by the runtime re-executing the input run's
   * own plan at the shifted window — the model never names the second run id,
   * so which period a growth rate compares against stays governed.
   */
  'period_over_period',
  /**
   * The least and greatest figures of one series, each with its coordinate.
   *
   * A SELECTION, not an arithmetic: the published value IS one of the named
   * figures, republished with the period or member it occurred at, so "which
   * month was biggest" binds to the governed cell itself (#8071, GL26/GL27).
   * Every tied point is published — a tie reported as its first member alone
   * would name a winner the data does not.
   */
  'extrema',
] as const;
export type RuntimeV3DeriveOperator = (typeof RUNTIME_V3_DERIVE_OPERATORS)[number];

/** One figure of one run, by the ref that run published it under. */
export type RuntimeV3DeriveInputRef = Readonly<{
  runId: string;
  valueRef: string;
}>;

/**
 * The plan as the caller writes it. It carries references and a constant, and
 * never a figure: `constant` is a multiplier the caller declares, not a value
 * read out of the data.
 */
export type RuntimeV3DerivePlan = Readonly<{
  operator: RuntimeV3DeriveOperator;
  inputs: readonly RuntimeV3DeriveInputRef[];
  /** `scale` only. An exact decimal, so the product is exact. */
  constant?: ExactDecimalString;
  /** `period_over_period` only. How many buckets of the run's own grain back. */
  lag?: number;
  /**
   * ALIGNED DERIVATION (#8846). Present only when the inputs are written
   * coordinate-major: `alignedGroups` groups of the operator's fixed arity,
   * each group one coordinate both source outputs published. Group 0 is the
   * lead figure; every later group publishes as a disclosure at its own
   * coordinate. Only an operator whose spec declares `alignable` admits it.
   */
  alignedGroups?: number;
}>;

/**
 * ONE RESOLVED OPERAND — the figure as the run store holds it.
 *
 * Everything here is copied off a published `RuntimeV3ResultValue`, so a
 * derived figure states its parts in the same words the run that produced them
 * did. `asOf` is carried because a difference between two figures read at two
 * different as-ofs is a different statement from one read at a single as-of,
 * and the reader is owed that distinction rather than a silent blend.
 */
export type RuntimeV3DeriveOperand = Readonly<{
  runId: string;
  /** The source run’s binding alias for this figure. Cell-indexed, not a metric. */
  valueRef: string;
  /**
   * THE MEASURE THIS OPERAND IS OF, at definition level (#8051, Astra check 4).
   *
   * Carried from the source run’s published value, never minted. Without it
   * a ratio’s denominator reaches the reconciliation as the literal role name
   * `derive-arithmetic.ts` supplies, and two ratios over different
   * denominators become one canonical claim.
   *
   * Optional for the same reason `metricRef` is optional on a result value:
   * a producer that never declared one must say so by absence.
   */
  metricRef?: string;
  label: string;
  exactValue: ExactDecimalString;
  unit: string;
  scopeLabel: string;
  periodLabel: string;
  asOf: IsoInstant | null;
  /** Dimension ref to member ref for the cell the figure came from. */
  coordinates: Readonly<Record<string, string>>;
  /**
   * #8100 HOLD cure 3e — THE CLASS THE SOURCE RUN PUBLISHED THIS FIGURE UNDER.
   *
   * Carried, never minted. `extrema` republishes an operand unchanged and must
   * republish its class unchanged too — an `observation` hardcoded regardless
   * of what the source run actually claimed would let a `scenario_estimate`
   * cross into `observation` merely by being selected. Every `RuntimeV3ResultValue`
   * declares one (`result-value-policy.ts`), so this is never optional on a
   * freshly resolved operand; it is optional here only because older callers in
   * this file's own tests construct an operand by hand without it.
   */
  resultClass?: RuntimeV3ResultClass;
  /**
   * WHAT THE RUN THAT PRODUCED THIS FIGURE SAID ABOUT WHO IS COUNTED.
   *
   * Carried, not summarised. When the runtime computes a denominator for
   * `share_of_total`, the additivity rules may lawfully narrow it — the
   * conditional rule (#7954) publishes a cross-commitment total for one
   * coherent promise set and names every overlay member it left out. A share
   * whose denominator quietly dropped four members is a number, not an answer,
   * so those sentences travel with the operand and are published with the
   * derived figure.
   */
  populationStatements?: readonly string[];
}>;

/**
 * #8351 PR 1 — ONE COMPILER-MINTED READ, inside a `composition` carrier.
 *
 * `operandId` is what the model actually named for the role; `recipe` is the
 * server-owned rule the compiler lowered it with, carried verbatim rather than
 * re-derived from the run id — an id names a run, never a rule.
 */
export type RuntimeV3DeriveCompositionNode = Readonly<{
  nodeId: string;
  ownerOutputId: string;
  role: string;
  operandId: string;
  recipe: RuntimeV3OperandRecipe;
}>;
export type RuntimeV3DeriveCompositionEdge = Readonly<{ from: string; to: string }>;

/**
 * THE COMPOSITION, as the derive tool's own input names it — SERVER-OWNED,
 * built by the analytical layer from resolution it already holds, never
 * inferred here from the run ids on the plan (#8351 Y5, Terra's exact-head
 * ruling on the v4 check). `nodes` and `edges` are sorted by id before this
 * reaches the run store, so two runs of the same request digest equal and a
 * reordered draft does not change the digest.
 */
export type RuntimeV3DeriveComposition = Readonly<{
  nodes: readonly RuntimeV3DeriveCompositionNode[];
  edges: readonly RuntimeV3DeriveCompositionEdge[];
  recipes: readonly RuntimeV3OperandRecipe[];
  revisionVector: readonly Readonly<{ datasetRef: string; revisionId: string }>[];
}>;

/**
 * What the accepted plan carries. Opaque to the run store, read only by the
 * derive executor.
 */
export type RuntimeV3DerivePlanBody = Readonly<{
  plan: RuntimeV3DerivePlan;
  /** The operands the CALLER named, in the order it named them. */
  operands: readonly RuntimeV3DeriveOperand[];
  /**
   * #8351 Y5 — present ONLY when this plan's inputs include a recipe-minted
   * internal read; a legacy derived run (no recipes) omits the member
   * entirely, so its accepted plan, store digest and claim hash stay
   * byte-identical to before PR 1 (asserted by the legacy byte guard).
   */
  composition?: RuntimeV3DeriveComposition;
  /**
   * THE OPERAND THE RUNTIME PRODUCED ITSELF, on the two operators that need
   * one: `share_of_total`'s governed denominator and `period_over_period`'s
   * prior-period figure. Both come from a run the runtime accepted and executed
   * from an already-admitted plan, never from anything the model wrote — which
   * is the whole reason neither a total nor a comparison period is a parameter
   * the model gets to choose.
   */
  runtimeOperand?: RuntimeV3DeriveOperand;
  /**
   * Sentences settled while the operands were resolved that are part of what
   * the derived figure means — which inputs it was computed from, that a
   * denominator is a governed total of a named set, and which inputs were read
   * at different as-ofs. They are published with the figure.
   */
  disclosures: readonly string[];
}>;

/**
 * The figure the RUNTIME must produce before an operator can compute anything.
 * A closed union so the builder for each is a table lookup: an operator cannot
 * acquire a fourth kind of self-supplied operand without a declaration here.
 */
export const RUNTIME_V3_DERIVE_RUNTIME_OPERANDS = [
  /** The operator works from the caller's inputs alone. */
  'none',
  /** The same plan, ungrouped and pinned to the listed members, run as a query. */
  'governed_total',
  /** The same plan at the window a declared number of buckets earlier. */
  'prior_period',
] as const;
export type RuntimeV3DeriveRuntimeOperandKind =
  (typeof RUNTIME_V3_DERIVE_RUNTIME_OPERANDS)[number];

/**
 * WHAT EACH OPERATOR REQUIRES, AS DATA.
 *
 * A frozen record keyed by the closed union, so admission is a lookup and
 * never a chain of string comparisons. Adding an operator means adding a row
 * here and a row in the compute table; it never means adding a branch.
 */
export type RuntimeV3DeriveOperatorSpec = Readonly<{
  /** Inclusive. `null` maximum means "any number at or above the minimum". */
  minInputs: number;
  maxInputs: number | null;
  requiresConstant: boolean;
  requiresLag: boolean;
  /** Whether every operand must carry one unit for the arithmetic to mean anything. */
  requiresOneUnit: boolean;
  /**
   * Whether the operands must be ONE SERIES: the cells of a single run's
   * breakdown, varying along exactly one dimension, each a distinct point.
   * A selection over figures that are not one series answers a question nobody
   * asked, so the operator refuses the shape rather than picking a winner.
   */
  requiresSeries: boolean;
  /** What the runtime must compute for itself before the operator can run. */
  runtimeOperand: RuntimeV3DeriveRuntimeOperandKind;
  /** Whether the operator may run once per aligned coordinate group in one run (#8846). */
  alignable: boolean;
  /** What the operator does, in the words the derived run publishes. */
  statement: string;
}>;

export const RUNTIME_V3_DERIVE_OPERATOR_SPECS: Readonly<
  Record<RuntimeV3DeriveOperator, RuntimeV3DeriveOperatorSpec>
> = Object.freeze({
  scale: Object.freeze({
    minInputs: 1,
    maxInputs: 1,
    requiresConstant: true,
    requiresLag: false,
    requiresOneUnit: false,
    requiresSeries: false,
    runtimeOperand: 'none',
    alignable: false,
    statement: 'one figure multiplied by a declared constant, in the figure\'s own unit',
  }),
  subtract: Object.freeze({
    minInputs: 2,
    maxInputs: 2,
    requiresConstant: false,
    requiresLag: false,
    // A difference of unlike units is not a smaller number; it is no number.
    requiresOneUnit: true,
    requiresSeries: false,
    runtimeOperand: 'none',
    alignable: true,
    statement: 'the first figure minus the second, both in one unit',
  }),
  ratio: Object.freeze({
    minInputs: 2,
    maxInputs: 2,
    requiresConstant: false,
    requiresLag: false,
    /**
     * A ratio of unlike units is a legitimate figure — a one-time cost per
     * unit of annual run rate is what a payback question asks for — so the
     * units are DISCLOSED rather than refused. Only a sum needs them to agree.
     */
    requiresOneUnit: false,
    requiresSeries: false,
    runtimeOperand: 'none',
    alignable: true,
    statement: 'the first figure over the second',
  }),
  share_of_total: Object.freeze({
    minInputs: 2,
    maxInputs: null,
    requiresConstant: false,
    requiresLag: false,
    requiresOneUnit: true,
    requiresSeries: false,
    runtimeOperand: 'governed_total',
    alignable: false,
    statement: 'the first figure over the governed total of every listed figure',
  }),
  period_over_period: Object.freeze({
    minInputs: 1,
    maxInputs: 1,
    requiresConstant: false,
    requiresLag: true,
    requiresOneUnit: true,
    requiresSeries: false,
    runtimeOperand: 'prior_period',
    alignable: false,
    statement: 'one figure against the same plan a declared number of buckets earlier',
  }),
  extrema: Object.freeze({
    minInputs: 2,
    maxInputs: null,
    requiresConstant: false,
    requiresLag: false,
    /**
     * A least or greatest across unlike units is not defined — no unit exists
     * for the answer — so it refuses `mixed_units` rather than picking one.
     */
    requiresOneUnit: true,
    requiresSeries: true,
    runtimeOperand: 'none',
    alignable: false,
    statement: 'the least and greatest of the named figures of one series, '
      + 'each republished with the coordinate it occurred at',
  }),
});

/** The scale every derived ratio is carried at, half-up, as the repo's ratios are. */
export const RUNTIME_V3_DERIVE_RATIO_SCALE = 4;

/** The unit a quotient carries. A ratio has no currency and no period. */
export const RUNTIME_V3_DERIVE_RATIO_UNIT = 'ratio';

export function isRuntimeV3DeriveOperator(
  candidate: string,
): candidate is RuntimeV3DeriveOperator {
  return (RUNTIME_V3_DERIVE_OPERATORS as readonly string[]).includes(candidate);
}
